import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Clock, MessageSquare, Send, CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function SettingsPage() {
  const [s, setS] = useState(null);
  const [testPhone, setTestPhone] = useState("");
  const [testChannel, setTestChannel] = useState("both");
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState(null);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const set = (k, v) => setS({ ...s, [k]: v });

  const sendTest = async () => {
    if (!testPhone.trim()) return toast.error("Enter a recipient phone number.");
    setTesting(true);
    setTestResults(null);
    try {
      const r = await api.post("/settings/test-message", { phone: testPhone.trim(), channel: testChannel });
      setTestResults(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    try {
      const r = await api.put("/settings", {
        assign_sla_hours: Number(s.assign_sla_hours),
        attend_sla_hours: Number(s.attend_sla_hours),
        recurring_window_days: Number(s.recurring_window_days),
        whatsapp_provider: s.whatsapp_provider, whatsapp_api_key: s.whatsapp_api_key,
        whatsapp_sender_id: s.whatsapp_sender_id, whatsapp_template_id: s.whatsapp_template_id,
        sms_provider: s.sms_provider, sms_api_key: s.sms_api_key,
        sms_sender_id: s.sms_sender_id, sms_template_id: s.sms_template_id,
      });
      setS(r.data);
      toast.success("Settings saved.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const Field = ({ k, label, type = "text", placeholder }) => (
    <div>
      <Label className="label-caps">{label}</Label>
      <Input type={type} value={s[k] ?? ""} data-testid={`settings-${k}`} placeholder={placeholder}
        onChange={(e) => set(k, e.target.value)} className="mt-1 rounded-sm" />
    </div>
  );

  return (
    <div className="p-5 sm:p-8 max-w-3xl fade-up">
      <div className="mb-6">
        <div className="label-caps text-muted-foreground">Configuration</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Settings</h1>
      </div>

      <section className="border border-border bg-white p-5 mb-5">
        <div className="flex items-center gap-2 label-caps text-klein mb-4"><Clock size={14} /> SLA thresholds</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field k="assign_sla_hours" label="Assign SLA (hours)" type="number" />
          <Field k="attend_sla_hours" label="Attend SLA (hours)" type="number" />
          <Field k="recurring_window_days" label="Recurring window (days)" type="number" />
        </div>
      </section>

      <section className="border border-border bg-white p-5 mb-5">
        <div className="flex items-center gap-2 label-caps text-klein mb-1"><MessageSquare size={14} /> WhatsApp messaging</div>
        <p className="text-xs text-muted-foreground mb-4">Leave blank to log messages to the activity log instead of sending. Providers: interakt, aisensy, gupshup.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field k="whatsapp_provider" label="Provider" placeholder="interakt" />
          <Field k="whatsapp_api_key" label="API key" />
          <Field k="whatsapp_sender_id" label="Sender ID / number" />
          <Field k="whatsapp_template_id" label="Template / campaign ID" />
        </div>
      </section>

      <section className="border border-border bg-white p-5 mb-5">
        <div className="flex items-center gap-2 label-caps text-klein mb-1"><MessageSquare size={14} /> SMS messaging</div>
        <p className="text-xs text-muted-foreground mb-4">Provider: msg91. Leave blank to log instead of sending.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field k="sms_provider" label="Provider" placeholder="msg91" />
          <Field k="sms_api_key" label="Auth key" />
          <Field k="sms_sender_id" label="Sender ID" />
          <Field k="sms_template_id" label="Flow / template ID" />
        </div>
      </section>

      <Button onClick={save} data-testid="save-settings-button" className="rounded-sm h-11 px-8 bg-klein hover:bg-klein/90 text-white font-semibold">
        Save settings
      </Button>

      <section className="border border-border bg-white p-5 mt-8" data-testid="test-message-section">
        <div className="flex items-center gap-2 label-caps text-klein mb-1"><Send size={14} /> Send test message</div>
        <p className="text-xs text-muted-foreground mb-4">
          Sends ONE real message using the currently <strong>saved</strong> credentials above (bypasses the
          activity-log fallback). Save your settings first, then test. The raw provider response is shown below.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-3 items-end">
          <div>
            <Label className="label-caps">Recipient phone</Label>
            <Input value={testPhone} data-testid="test-phone-input" placeholder="e.g. 919876543210"
              onChange={(e) => setTestPhone(e.target.value)} className="mt-1 rounded-sm" />
          </div>
          <div>
            <Label className="label-caps">Channel</Label>
            <Select value={testChannel} onValueChange={setTestChannel}>
              <SelectTrigger data-testid="test-channel-select" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="both">WhatsApp + SMS</SelectItem>
                <SelectItem value="whatsapp">WhatsApp only</SelectItem>
                <SelectItem value="sms">SMS only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={sendTest} disabled={testing} data-testid="send-test-button"
            className="rounded-sm h-10 bg-ink hover:bg-ink/90 text-white font-semibold">
            {testing ? <Loader2 className="animate-spin mr-1" size={16} /> : <Send size={15} className="mr-1" />}
            Send test
          </Button>
        </div>

        {testResults && (
          <div className="mt-5 space-y-3" data-testid="test-results">
            <div className="text-xs text-muted-foreground">
              To <span className="font-mono">{testResults.phone}</span> · message: “{testResults.message}”
            </div>
            {testResults.results.map((r, i) => {
              const success = r.ok === true;
              const failed = r.error || r.ok === false || r.configured === false;
              return (
                <div key={i} data-testid={`test-result-${r.channel?.toLowerCase()}`}
                  className={`border p-4 ${success ? "border-emerald-500 bg-emerald-50" : failed ? "border-signal bg-signal/10" : "border-border"}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 font-bold">
                      {success ? <CheckCircle2 size={16} className="text-emerald-600" /> : <XCircle size={16} className="text-signal" />}
                      {r.channel}
                      {r.provider && <span className="text-xs font-normal text-muted-foreground">via {r.provider}</span>}
                    </div>
                    {r.status_code != null && (
                      <span className={`font-mono text-xs font-bold px-2 py-0.5 ${success ? "bg-emerald-600 text-white" : "bg-signal text-white"}`}>
                        HTTP {r.status_code}
                      </span>
                    )}
                  </div>

                  {r.configured === false && (
                    <div className="text-sm text-signal mt-2">{r.error}</div>
                  )}
                  {r.error && r.configured !== false && (
                    <div className="text-sm text-signal mt-2 font-mono break-all">{r.error}</div>
                  )}
                  {r.message_id && (
                    <div className="text-sm mt-2">Message ID: <span className="font-mono font-semibold">{r.message_id}</span></div>
                  )}
                  {r.response != null && (
                    <pre className="mt-2 text-xs bg-ink text-white p-3 overflow-x-auto rounded-sm whitespace-pre-wrap break-all" data-testid={`raw-response-${r.channel?.toLowerCase()}`}>
{typeof r.response === "string" ? r.response : JSON.stringify(r.response, null, 2)}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
